import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, XCircle, Info, CheckCircle2 } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/utils/cn';

export type AlertModalVariant = 'error' | 'warning' | 'info' | 'success';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: AlertModalVariant;
  title: string;
  description?: string;
  details?: string;
  confirmLabel?: string;
  onConfirm?: () => void;
}

const ICONS: Record<AlertModalVariant, typeof AlertTriangle> = {
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
};

const ICON_STYLES: Record<AlertModalVariant, string> = {
  error: 'text-red-600 bg-red-100',
  warning: 'text-amber-600 bg-amber-100',
  info: 'text-blue-600 bg-blue-100',
  success: 'text-green-600 bg-green-100',
};

export function AlertModal({
  open,
  onOpenChange,
  variant = 'error',
  title,
  description,
  details,
  confirmLabel = 'Entendi',
  onConfirm,
}: Props) {
  const Icon = ICONS[variant];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-2xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <div className="flex items-start gap-4">
            <div className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-full', ICON_STYLES[variant])}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="flex-1 pt-1">
              <Dialog.Title className="text-lg font-semibold text-foreground">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-2 text-sm text-muted-foreground whitespace-pre-line">
                  {description}
                </Dialog.Description>
              )}
              {details && (
                <pre className="mt-3 max-h-40 overflow-auto rounded-md bg-muted p-3 text-xs text-muted-foreground whitespace-pre-wrap">
                  {details}
                </pre>
              )}
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              variant={variant === 'error' ? 'destructive' : 'default'}
              onClick={() => {
                onConfirm?.();
                onOpenChange(false);
              }}
              autoFocus
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
